import type { TDictionaryItem } from "./profileRequest"
import { TActiveUserResponse, TUserAuthorRef } from "./users"

// Создание черновика проекта
export interface ICreateProject {
    name: string
    type: string
    stage: string
    region: string
    description: string
    full_description?: string | null
    env_factor: string
    expected_result: string
    initial_state: string
    necessary_actions: string
    implementation: string
    /** Optional link to published SocialOrder (create-only). */
    social_order_id?: number
}

/** Nested source SO on ProjectResource (create/show). */
export interface IProjectSourceSocialOrder {
    id: number
    title: string
    slug: string
    image: string | null
}

/** UserShortResource в ответе ProjectResource — минимальное подмножество для клиента */
export interface IProjectAuthorShort {
    id: number | null
    slug: string | null
    shortname?: string | null
    fullname?: string | null
    /** На бэкенде приходит как MediaResource-like объект; в api-мэппинге нормализуем до URL. */
    avatar?: string | null
    is_online?: boolean
    deleted?: boolean
}

export interface IProjectModerationUserCandidate extends IProjectAuthorShort {
    /** Stable id (prefer over user.id when stub has id: null). */
    user_id: number
    restrictable: boolean
    restrictable_reason?: string | null
}

export interface IProjectModerationUserCandidatesResponse {
    items: IProjectModerationUserCandidate[]
    total: number
}

export interface IProjectAboutResource {
    description: string | null
    full_description?: string | null
    env_factor: string | null
    expected_result: string | null
    initial_state: string | null
    necessary_actions: string | null
    implementation: string | null
}

/** Разбивка комментариев по типам (GET проекта по slug в ProjectResource). */
export interface IProjectCommentCountersByType {
    total: number
    opinions: number
    suggestions: number
    questions: number
}

/** Счётчики для трёх вкладок фильтра комментариев. */
export type ProjectCommentTabCounts = {
    opinions: number
    suggestions: number
    questions: number
}

export interface IProjectCountersResource {
    likes: number
    views: number
    members: number
    subscribers: number
    /** Список карточек: число; детальная карточка: объект по типам. */
    comments: number | IProjectCommentCountersByType
}

/** Для бейджа «всего комментариев» в списке проектов. */
export function resolveProjectCommentsTotal(counters: IProjectCountersResource["comments"]): number {
    if (typeof counters === "number") return counters
    if (counters && typeof counters === "object") return counters.total ?? 0
    return 0
}

export function resolveProjectCommentTabCounts(
    comments: IProjectCountersResource["comments"] | undefined | null
): ProjectCommentTabCounts {
    if (comments && typeof comments === "object") {
        return {
            opinions: Number(comments.opinions) || 0,
            suggestions: Number(comments.suggestions) || 0,
            questions: Number(comments.questions) || 0,
        }
    }
    return { opinions: 0, suggestions: 0, questions: 0 }
}

export interface IProjectViewerPermissions {
    is_owner: boolean
    can_edit_content: boolean
    can_moderate: boolean
    can_manage_admins: boolean
    can_manage_team: boolean
    can_delete_project: boolean
    can_comment: boolean
    can_view_team_space: boolean
    restriction_level: 'full' | 'comments' | null
}

export interface IProjectTeamBoardPostResource {
    user: IProjectAuthorShort
    user_id: number
    body: string
    telegram: string | null
    updated_at: string
    created_at: string
}

export interface IProjectTeamSpaceResource {
    links: string[]
    posts: IProjectTeamBoardPostResource[]
}

export interface IProjectViewerResource {
    is_followed: boolean
    is_joined: boolean
    is_liked?: boolean
    permissions?: IProjectViewerPermissions
}

export type ProjectUserRestrictionLevel = 'full' | 'comments'

export interface IProjectUserRestrictionResource {
    user: IProjectAuthorShort
    /** Stable id for DELETE (even when user stub has id: null). */
    user_id: number
    level: ProjectUserRestrictionLevel
    reason: string | null
    created_at: string
    created_by: IProjectAuthorShort
}

export interface IUpdateProjectAdminsBody {
    admin_user_ids: number[]
}

export type ProjectTeamRole = 'author' | 'coauthor' | 'member'

export interface IProjectTeamMemberResource {
    user: IProjectAuthorShort
    /** Stable membership key (real user id even when user stub has id: null). */
    user_id: number
    permission_role: 'member' | 'admin'
    team_role: 'member' | 'coauthor' | 'author'
    role_label: string | null
    is_featured: boolean
    sort_order: number
    is_author: boolean
    source_team: IProjectMemberSourceTeam | null
}

export interface IProjectMemberSourceTeam {
    id: number
    slug: string
    name: string
}

export type ProjectTeamInvitationStatus = 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired'

export interface IProjectTeamInvitationTeamResource {
    id: number
    name: string
    slug: string
    image?: string | null
}

export interface IProjectTeamInvitationProjectResource {
    id: number
    name: string
    slug: string
    image?: string | null
}

export interface IProjectTeamInvitationResource {
    id: number
    project_id: number
    project?: IProjectTeamInvitationProjectResource | null
    team_id: number
    team?: IProjectTeamInvitationTeamResource | null
    inviter?: IProjectAuthorShort | null
    status: ProjectTeamInvitationStatus
    message: string | null
    response_message: string | null
    expires_at: string | null
    decided_at: string | null
    created_at: string
    updated_at: string
}

export interface IProjectParticipatingTeam {
    id: number
    slug: string
    name: string
    slogan?: string | null
    image?: string | null
    counters?: {
        members?: number
        views?: number
        subscribers?: number
    }
}

export type IProjectNeedSlotKind = 'role' | 'resource'
export type IProjectNeedSlotStatus = 'open' | 'paused' | 'closed'

export interface IProjectNeedSlotMeta {
    quantity?: string | null
    acquisition_type?: string | null
    duration_type?: string | null
    delivery_type?: string | null
    budget_type?: string | null
    budget_comment?: string | null
    requirements?: string | null
}

export interface IProjectNeedSlotResource {
    id: number
    project_id: number
    kind: IProjectNeedSlotKind
    title: string
    description: string | null
    status: IProjectNeedSlotStatus
    city: string | null
    region: string | null
    remote_allowed: boolean
    expires_at: string | null
    meta: IProjectNeedSlotMeta
    competencies: TDictionaryItem[]
    resources: TDictionaryItem[]
    participation: TDictionaryItem[]
    created_at?: string
    updated_at?: string
}

export interface IProjectNeedSlotMutationBody {
    kind?: IProjectNeedSlotKind
    title?: string
    description?: string | null
    status?: IProjectNeedSlotStatus
    city?: string | null
    region?: string | null
    remote_allowed?: boolean
    expires_at?: string | null
    meta?: IProjectNeedSlotMeta | null
    competency_ids?: number[]
    resource_ids?: number[]
    participation_ids?: number[]
}

export interface IProjectTeamPreviewItem {
    user: IProjectAuthorShort
    team_role: ProjectTeamRole
    role_label: string | null
    is_author: boolean
}

export interface IUpdateProjectTeamMemberInput {
    user_id: number
    team_role: 'member' | 'coauthor'
    role_label: string | null
    is_featured: boolean
    sort_order: number
}

export interface IUpdateProjectTeamAuthorDisplay {
    role_label: string | null
    is_featured: boolean
    sort_order: number
}

export interface IUpdateProjectTeamBody {
    author_display: IUpdateProjectTeamAuthorDisplay
    members: IUpdateProjectTeamMemberInput[]
}

export interface IProjectAuthorDisplay {
    role_label: string | null
    is_featured: boolean
    sort_order: number
}

/** JSON тела ответа POST /projects (ProjectResource), не путать с entity Project */
export interface IProjectResourceResponse {
    id: number
    name: string
    type: string
    slug: string
    stage: string
    status: string
    website: string | null
    address: string | null
    city: string | null
    region: string | null
    location: string | null
    latitude: number | null
    longitude: number | null
    /** Project preview. В v1 бэкенда приходит как MediaResource-like объект; на клиенте нормализуем до URL. */
    image: string | null
    /** Gallery. Сейчас может быть null; при наличии нормализуем до массива URL. */
    image_gallery: string[] | null
    /** whenLoaded('author') — ключ может отсутствовать */
    author?: IProjectAuthorShort | null
    source_social_order?: IProjectSourceSocialOrder | null
    published_at: string | null
    created_at: string
    updated_at: string
    about: IProjectAboutResource
    counters: IProjectCountersResource
    viewer?: IProjectViewerResource | null
}


// получение массива проектов 
export interface IProjectsListItem {
    id: number
    about: {
        description: string | null
    }
    address: string | null
    author_id: number
    /** whenLoaded('author') — ключ может отсутствовать */
    author?: IProjectAuthorShort | null
    city: string | null
    counters: IProjectCountersResource
    /** Preview. В ответе может прийти MediaResource-like объект; нормализуем до URL. */
    image: string | null
    latitude: string | null
    location: string | null
    longitude: string | null
    name: string
    published_at: string | null
    region: string | null
    slug: string
    stage: number
    status: string | null
    type: string | null
    website: string | null
    /** Подписан ли текущий пользователь (если бэк отдаёт это поле). */
    is_followed?: boolean
    /** Участник ли текущий пользователь (если бэк отдаёт это поле). */
    is_joined?: boolean
    /** Блок viewer (если бэк отдаёт его). */
    viewer?: IProjectViewerResource | null
}

export type TProjectsListResponse = IProjectsListItem[]

/** GET /projects — только непустые ключи уходят в query; без status (бэкенд не принимает). */
export type TGetProjectsQueryParams = {
    query?: string
    type?: string
    stage?: string
    tag?: string
    sort_by?: 'published_at' | 'created_at'
    sort_direction?: 'asc' | 'desc'
}

export type TGetProjectsCursorQueryParams = TGetProjectsQueryParams & {
    cursor?: string | null
    limit?: number
}

export type TCursorMeta = {
    next_cursor: string | null
}

export type TProjectsCursorPage = {
    items: TProjectsListResponse
    meta: TCursorMeta
}

export type TProjectsStatsResponse = {
    projects_total_count: number
    projects_published_count: number
    projects_unpublished_count: number
}


// получение самого проекта 
export interface IProjectResponse {
    id: number
    about: IProjectAboutResource
    address: string | null
    /** В ProjectResource приходит UserShortResource (не полный UserResource). */
    author: IProjectAuthorShort
    /** Топ-3 последних лайкнувших (newest first). */
    likes_preview?: IProjectAuthorShort[] | null
    team_preview?: IProjectTeamPreviewItem[] | null
    participating_teams?: IProjectParticipatingTeam[] | null
    source_social_order?: IProjectSourceSocialOrder | null
    author_display?: IProjectAuthorDisplay | null
    city: string | null
    counters: IProjectCountersResource
    created_at: string
    /** Preview. В ответе может прийти MediaResource-like объект; нормализуем до URL. */
    image: string | null
    /** Gallery. Сейчас может быть null; при наличии нормализуем до массива URL. */
    image_gallery: string[] | null
    latitude: string | null
    location: string | null
    longitude: string | null
    name: string
    published_at: string | null
    region: string | null
    slug: string
    stage: number
    status: string | null
    type: string
    updated_at: string
    website: string | null
    tags?: string[] | null,
    /** Дополнительные внешние ссылки (соцсети, каналы). Только detail. */
    external_links?: string[] | null,
    /** Подписан ли текущий пользователь (если бэк отдаёт это поле). */
    is_followed?: boolean
    /** Участник ли текущий пользователь (если бэк отдаёт это поле). */
    is_joined?: boolean
    /** Блок viewer (если бэк отдаёт это поле). */
    viewer?: IProjectViewerResource | null
}

/** UserShortResource для списка лайков проекта. */
export interface IProjectLikeUserResource {
    id: number | null
    slug?: string | null
    shortname?: string | null
    fullname?: string | null
    avatar?: string | null
    deleted?: boolean
    name?: string | null
}


// изменение описания проекта 
// TODO: прапивльно прописать в зависимости что конкретно нужно и что обязательно
export interface IUpdateProjectDescriptionBody {
    name: string,
    type: string,
    stage: string,
    status: string,
    website?: string | null,
    address?: string | null,
    city?: string | null,
    region: string | null,
    latitude?: number | null,
    longitude?: number | null,
    description: string,
    full_description?: string | null,
    env_factor: string,
    expected_result: string,
    initial_state: string,
    necessary_actions: string,
    implementation: string,
    published_at?: string | null,
    meta?: Record<string, unknown> | null,
    tags?: string[] | null,
    external_links?: string[] | null,
}


// статусы шагов плана действий
export type TProjectStepStatus =
    | 'draft'
    | 'awaiting'
    | 'in_progress'
    | 'paused'
    | 'canceled'
    | 'done'


// создание шага плана действий
export interface ICreateProjectStepBody {
    name: string
    description: string
    started_at?: string | null
    finished_at?: string | null
    responsible_user_ids?: number[] | null
    status?: TProjectStepStatus
}

export interface IUpdateProjectStepBody {
    name?: string
    description?: string
    started_at?: string | null
    finished_at?: string | null
    responsible_user_ids?: number[] | null
    status?: TProjectStepStatus
}


// получение шага плана действий
export interface IProjectStepResource {
    id: number
    project_id: number
    responsibles?: TActiveUserResponse[] | null
    name: string
    status: TProjectStepStatus
    description: string
    started_at: string | null
    finished_at: string | null
}


/** Сниппет адресата при ответе «плоской» веткой (ответ на L3 и legacy). */
export interface IGetCommentReplyToPreview {
    author_first_name: string
    author_last_name: string
    text_preview: string
}

// получение комментариев к проекту
export interface IGetCommentResponse {
    id: number
    author: TUserAuthorRef
    children?: IGetCommentResponse[]
    counters: {
        children: number
        likes: number
        dislikes: number
    }
    /** Реакция текущего пользователя: 1 лайк, 0 нет, -1 дизлайк (бэкенд); в UI комментариев используем только лайк. */
    my_reaction?: number | null
    viewer?: {
        can_delete?: boolean
    }
    edited: boolean
    edited_at: string | null
    parent_id?: number | null
    reply_to_comment_id?: number | null
    reply_to?: IGetCommentReplyToPreview | null
    published_at: string | null
    text: string
    type: string
}

export type TGetCommentsResponse = IGetCommentResponse[]

/** UI sort in CommentsFilter (sort field). */
export type CommentsSortUi = 'new' | 'old' | 'interesting'

/** GET комментариев проекта; без parent_id — корни ленты (кэш RTK по этому ключу). */
export type GetCommentsQueryArg = {
    projectId: number
    type: string
    parentId?: number
    limit?: number
    cursor?: string | null
    sort?: CommentsSortUi
}


// добавление комментария к проекту
export interface IAddCommentToProject {
    text: string,
    parent_id?: number | null,
}

/** PUT /comments/{id}/update */
export interface IUpdateCommentBody {
    text: string
}

/** PUT /comments/{id}/reaction */
export interface ISetCommentReactionBody {
    value: 0 | 1
}
